import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Plus, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import AiSummarizeButton from "@/components/AiSummarizeButton";

const SEVERITY = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-rose-100 text-rose-700",
};

const BLANK = { property_id: "", title: "", description: "", severity: "medium", incident_date: "" };

export default function IncidentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "landlord";

  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const [i, p] = await Promise.all([api.get("/incidents"), api.get("/properties")]);
    setItems(i.data); setProperties(p.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({
      property_id: i.property_id || "",
      title: i.title || "",
      description: i.description || "",
      severity: i.severity || "medium",
      incident_date: i.incident_date || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/incidents/${editing.id}`, form);
        toast.success("Incident updated");
      } else {
        await api.post("/incidents", form);
        toast.success("Incident recorded");
      }
      setOpen(false); setForm(BLANK); setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/incidents/${confirmDelete.id}`);
      toast.success("Incident deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AppLayout
      title="Incident Reports"
      subtitle="Document any incident with date, severity & details"
      actions={
        <Button data-testid="add-incident-button" onClick={openCreate} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none h-11 px-5 uppercase tracking-[0.2em] text-xs font-semibold gap-2 transition-colors duration-300">
          <Plus className="h-4 w-4" /> Report Incident
        </Button>
      }
    >
      {items.length === 0 ? (
        <EmptyState
          kind="incidents"
          eyebrow="All Calm"
          title="No incidents recorded."
          body="Nothing of concern to log. When an incident occurs, document it here with severity and date so your record stays complete."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="incidents-list">
          {items.map(i => (
            <Card key={i.id} data-testid={`incident-${i.id}`} className="p-5 border-slate-200 hover:border-gold/60 transition-colors">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-rose-50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-5 w-5 text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-display font-semibold">{i.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${SEVERITY[i.severity]}`}>{i.severity}</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-2 whitespace-pre-wrap">{i.description}</p>
                  <div className="text-xs text-slate-500">
                    {i.property_name} · {i.incident_date} · {i.reporter_name}
                    {i.updated_by_username && <span className="ml-2 italic">(edited by @{i.updated_by_username})</span>}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button onClick={() => openEdit(i)} variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-incident-${i.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    <Button onClick={() => setConfirmDelete(i)} variant="ghost" size="icon" className="h-8 w-8" data-testid={`delete-incident-${i.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(BLANK); } }}>
        <DialogContent data-testid="incident-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit incident" : "Record an incident"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Property</Label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="inc-property-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="inc-title" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Incident date</Label><Input type="date" required value={form.incident_date} onChange={e => setForm({ ...form, incident_date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Severity</Label>
              <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="inc-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Description</Label>
                {isAdmin && (
                  <AiSummarizeButton
                    kind="incident"
                    title={form.title}
                    text={form.description}
                    onApplySeverity={(sev) => setForm((f) => ({ ...f, severity: sev }))}
                  />
                )}
              </div>
              <Textarea data-testid="inc-description" required rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="inc-submit" type="submit" disabled={saving} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none uppercase tracking-[0.2em] text-xs font-semibold">
                {saving ? "Saving…" : (editing ? "Save changes" : "Save incident")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-incident-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this incident?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && <>This will permanently remove <strong>{confirmDelete.title}</strong>. This action cannot be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-incident" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
